import { AlertTitle, Box, Button, Card, CardContent, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, styled, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, useTheme } from '@mui/material';
import { FC, Fragment, useContext, useMemo, useState, } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import AuthContext from '../../contexts/auth/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import UserGroupsContext from '../../contexts/data/UserGroupsContext';
import { CommunityGroupMemberStatus, PostCommunityGroupMemberDownloadRights, PostCommunityGroupMemberViewRights, PostRemoveCommunityGroupMember, SiteEventType } from '../../api/dtos/dtos';
import MapContext from '../../contexts/data/MapContext';
import dayjs from 'dayjs';
import { SpeciesContext } from '../../contexts/data/SpeciesContext';
import useGetSiteEventGetCollection from '../../hooks/data/useGetSiteEventGetCollection';
import ServiceStackClientContext from '../../contexts/api/ServiceStackContext';
import AlertContext from '../../contexts/styles/AlertContext';
import { ERROR_TIMEOUT } from '../../constants/styles/AlertConstants';
import { LoadingButton} from '@mui/lab';
import useGetAllCommunityGroupMembers from '../../hooks/data/useGetAllCommunityGroupMembers';
import { faArrowLeft, faSend, faTrash } from '@fortawesome/pro-duotone-svg-icons';

const ContentWrapper = styled(Box)(({ theme }) => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.background.default,
}));

const ManageGroupMembersComponent: FC = () => {
    //
    // -- Hooks --
    let navigate = useNavigate();
    const theme = useTheme();

    //
    // -- Params
    const { species: speciesName } = useParams();

    //
    // -- Contexts --
    const { data: userGroupsConfig } = useContext(UserGroupsContext);
    const { filter } = useContext(MapContext);
    const { species } = useContext(SpeciesContext);
    const client = useContext(ServiceStackClientContext);
    const { addAlert } = useContext(AlertContext);

    //
    // -- States --
    const [selectedCommunityGroupStatus, setSelectedCommunityGroupStatus] = useState<number>(1);
    const [downloadRightsLoading, setDownloadRightsLoading] = useState<Record<number, boolean>>({});
    const [viewRightsLoading, setViewRightsLoading] = useState<Record<number, boolean>>({});
    const [removeCommunityGroupMemberLoading, setRemoveCommunityGroupMemberLoading] = useState<Record<number, boolean>>({});
    const [searchInput, setSearchInput] = useState<string>("");

    const handleChange = (event: SelectChangeEvent) => {
        setSelectedCommunityGroupStatus(Number(event.target.value));
    };

    const handleSearchInputChange = (event: any) => {
        setSearchInput(event.target.value);
    };

    // Generate the community group members request
    const communityGroupMembersRequest = useMemo(() => {
        if (filter.bySubmitter?.communityGroupId){
            return {
                communityGroupId: filter.bySubmitter.communityGroupId,
            };
        }
    }, [filter]);

    // Get community group members requests
    const { data: communityGroupMembers, loading: communityGroupMembersLoading, refresh: useGetAllCommunityGroupMembersRefresh }  = useGetAllCommunityGroupMembers(
        communityGroupMembersRequest, 
        { revalidateOnFocus: false, errorRetryCount: 0 }
    );

    // Generate the site events request based off map filter values
    const siteEventsRequest = useMemo(() => {
        if (filter.bySubmitter?.communityGroupId && species.species){
            return {
                species: species.species,
                memberOfCommunityGroupIds: [filter.bySubmitter.communityGroupId],
                startDateOnOrAfter: dayjs("2000-01-01").format("YYYY-MM-DD"),
                startDateOnOrBefore: dayjs().format("YYYY-MM-DD")
            };
        }
        return undefined;
    }, [filter, species]);

    // Get site event requests
    const { data: siteEventsSighting, loading: siteEventsSightingLoading }  = useGetSiteEventGetCollection(
        siteEventsRequest ? {...siteEventsRequest, siteEventType: SiteEventType.Sighting} : undefined,
        { revalidateOnFocus: false, errorRetryCount: 0 }
    );
    const { data: siteEventsDamage, loading: siteEventsDamageLoading } = useGetSiteEventGetCollection(
        siteEventsRequest ? {...siteEventsRequest, siteEventType: SiteEventType.Damage} : undefined,
        { revalidateOnFocus: false, errorRetryCount: 0 }
    );
    const { data: siteEventsControl, loading: siteEventsControlLoading } = useGetSiteEventGetCollection(
        siteEventsRequest ? {...siteEventsRequest, siteEventType: SiteEventType.Control} : undefined,
        { revalidateOnFocus: false, errorRetryCount: 0 }
    );

    const toggleDownloadRights = async (userId: number | undefined, communityGroupId: number, value: boolean) => {
        if (userId === undefined) {
            return;
        }

        setDownloadRightsLoading(c => ({...c, [userId]: true}));

        try {
            await client.post(new PostCommunityGroupMemberDownloadRights({
                userId,
                communityGroupId,
                downloadRights: value
            }));

            await useGetAllCommunityGroupMembersRefresh();
        }
        catch {
            addAlert({
                content: <AlertTitle>
                            An error occurred while attempting to set download rights.
                        </AlertTitle>,
                dismissable: true,
                severity: 'error',
                timeout: ERROR_TIMEOUT
            });
        }
        finally {
            setDownloadRightsLoading(c => ({...c, [userId]: false}))
        }
    };

    const toggleViewRights = async (userId: number | undefined, communityGroupId: number, value: boolean) => {
        if (userId === undefined) {
            return;
        }

        setViewRightsLoading(c => ({...c, [userId]: true}));

        try {
            await client.post(new PostCommunityGroupMemberViewRights({
                userId,
                communityGroupId,
                viewRights: value
            }));

            await useGetAllCommunityGroupMembersRefresh();
        }
        catch {
            addAlert({
                content: <AlertTitle>
                            An error occurred while attempting to set view rights.
                        </AlertTitle>,
                dismissable: true,
                severity: 'error',
                timeout: ERROR_TIMEOUT
            })
        }
        finally {
            setViewRightsLoading(c => ({...c, [userId]: false}));
        }
    };

    const removeCommunityGroupMember = async (userId: number | undefined, communityGroupId: number) => {
        if (userId === undefined) {
            return;
        }

        setRemoveCommunityGroupMemberLoading(c => ({...c, [userId]: true}));

        try {
            await client.post(new PostRemoveCommunityGroupMember({
                userId,
                communityGroupId
            }));

            await useGetAllCommunityGroupMembersRefresh();
        }
        catch {
            addAlert({
                content: <AlertTitle>
                            An error occurred while attempting to remove a group member.
                        </AlertTitle>,
                dismissable: true,
                severity: 'error',
                timeout: ERROR_TIMEOUT
            })
        }
        finally {
            setRemoveCommunityGroupMemberLoading(c => ({...c, [userId]: false}));
        }
    };

    // Generate table header
    const tableHeadersCells = useMemo(() => {
        switch(selectedCommunityGroupStatus){
            case CommunityGroupMemberStatus.Accepted:
                return (
                    <>
                        <TableCell align="left"><Typography fontSize={16} fontWeight={600}>Email</Typography></TableCell>
                        <TableCell align="left"><Typography fontSize={16} fontWeight={600}>Name</Typography></TableCell>
                        <TableCell align="left"><Typography fontSize={16} fontWeight={600}>Date Invited</Typography></TableCell>
                        <TableCell align="center"><Typography fontSize={16} fontWeight={600}>Sighting</Typography></TableCell>
                        <TableCell align="center"><Typography fontSize={16} fontWeight={600}>Damage</Typography></TableCell>
                        <TableCell align="center"><Typography fontSize={16} fontWeight={600}>Control</Typography></TableCell>
                        <TableCell align="center"><Typography fontSize={16} fontWeight={600}>Delete</Typography></TableCell>
                        <TableCell align="center"><Typography fontSize={16} fontWeight={600}>Download Rights</Typography></TableCell>
                        <TableCell align="center"><Typography fontSize={16} fontWeight={600}>View Rights</Typography></TableCell>
                    </>
                );
            case CommunityGroupMemberStatus.Pending:
                return (
                    <>
                        <TableCell align="left"><Typography fontSize={16} fontWeight={600}>Email</Typography></TableCell>
                        <TableCell align="left"><Typography fontSize={16} fontWeight={600}>Name</Typography></TableCell>
                        <TableCell align="left"><Typography fontSize={16} fontWeight={600}>Date Invited</Typography></TableCell>
                        <TableCell align="center"><Typography fontSize={16} fontWeight={600}>Remove</Typography></TableCell>
                    </>
                );
            default:
                return (
                    <>
                        <TableCell align="left"><Typography fontSize={16} fontWeight={600}>Email</Typography></TableCell>
                        <TableCell align="left"><Typography fontSize={16} fontWeight={600}>Name</Typography></TableCell>
                        <TableCell align="left"><Typography fontSize={16} fontWeight={600}>Date Invited</Typography></TableCell>
                        <TableCell align="left"><Typography fontSize={16} fontWeight={600}>Date Left</Typography></TableCell>
                    </>
                );
        }
    }, [selectedCommunityGroupStatus]);
    
    const tableBody = useMemo(() => {
        const members = communityGroupMembers?.communityGroupMembers ?? [];

        const filteredMembers = members
            .filter(m => m.communityGroupStatusId === selectedCommunityGroupStatus)
            .filter(m => {
                if (!searchInput) return true;

                const search = searchInput.toLowerCase();
                return (
                    m?.user?.fullName?.toLowerCase().includes(search) ||
                    m?.email?.toLowerCase().includes(search)
                );
            });
            
        switch (selectedCommunityGroupStatus) {
            case CommunityGroupMemberStatus.Accepted:
                return filteredMembers.map(m => (
                    <Fragment key={`accepted-${m.userId}`}>
                        <TableRow>
                            <TableCell>{m.email}</TableCell>
                            <TableCell>{m.user?.fullName}</TableCell>
                            <TableCell>
                                {dayjs(m.dateEntered).format("DD/MM/YYYY")}
                            </TableCell>

                            <TableCell align="center">
                                {siteEventsSighting?.siteEvents?.filter(se => se.userId === m.userId).length ?? 0}
                            </TableCell>

                            <TableCell align="center">
                                {siteEventsDamage?.siteEvents?.filter(se => se.userId === m.userId).length ?? 0}
                            </TableCell>

                            <TableCell align="center">
                                {siteEventsControl?.siteEvents?.filter(se => se.userId === m.userId).length ?? 0}
                            </TableCell>

                            <TableCell align="center">
                                <LoadingButton
                                    loading={removeCommunityGroupMemberLoading[m.userId!]}
                                    size="small"
                                    onClick={() => removeCommunityGroupMember(m.userId, m.communityGroupId)}
                                >
                                    <FontAwesomeIcon
                                        icon={faTrash}
                                        style={{
                                            fontSize: "1.2rem",
                                            color: theme.palette.primary.main
                                        }}
                                    />
                                </LoadingButton>
                            </TableCell>

                            <TableCell align="center">
                                <LoadingButton
                                    loading={downloadRightsLoading[m.userId!]}
                                    size="small"
                                    onClick={() => toggleDownloadRights(m.userId, m.communityGroupId, !m.downloadRights)}
                                >
                                    {m.downloadRights ? "Remove" : "Add"}
                                </LoadingButton>
                            </TableCell>

                            <TableCell align="center">
                                <LoadingButton
                                    loading={viewRightsLoading[m.userId!]}
                                    size="small"
                                    onClick={() => toggleViewRights(m.userId, m.communityGroupId, !m.viewRights)}
                                >
                                    {m.viewRights ? "Remove" : "Add"}
                                </LoadingButton>
                            </TableCell>
                        </TableRow>
                    </Fragment>
                ));

            case CommunityGroupMemberStatus.Pending:
                return filteredMembers.map(m => (
                    <Fragment key={`pending-${m.userId}`}>
                        <TableRow>
                            <TableCell>{m.email}</TableCell>
                            <TableCell>{m.user?.fullName}</TableCell>
                            <TableCell>
                                {dayjs(m.dateEntered).format("DD/MM/YYYY")}
                            </TableCell>

                            <TableCell align="center">
                            <LoadingButton
                                loading={removeCommunityGroupMemberLoading[m.userId!]}
                                size="small"
                                onClick={() => removeCommunityGroupMember(m.userId, m.communityGroupId)}
                            >
                                <FontAwesomeIcon
                                    icon={faTrash}
                                    style={{
                                        fontSize: "1.2rem",
                                        color: theme.palette.primary.main
                                    }}
                                />
                            </LoadingButton>
                            </TableCell>
                        </TableRow>
                    </Fragment>
                ));

            default:
                return filteredMembers.map(m => (
                    <Fragment key={`default-${m.userId}`}>
                        <TableRow>
                            <TableCell>{m.email}</TableCell>
                            <TableCell>{m.user?.fullName}</TableCell>
                            <TableCell>{dayjs(m.dateEntered).format("DD/MM/YYYY")}</TableCell>
                            <TableCell>{dayjs(m.dateLeftGroup).format("DD/MM/YYYY")}</TableCell>
                        </TableRow>
                    </Fragment>
                ));
        }
    }, [
        selectedCommunityGroupStatus,
        communityGroupMembers,
        searchInput,
        siteEventsSighting,
        siteEventsDamage,
        siteEventsControl
    ]);

    return (
        <Box>
            <ContentWrapper>
                {/* Header */}
                <Typography fontSize={32} color="#FFFFFF" sx={{paddingTop: '15px'}}>Manage Group Members</Typography>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'start',
                        color: 'white'
                    }}
                >
                    <Button
                        variant="text"
                        sx={{
                            color: 'white',
                            fontSize: '0.65rem',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.15)'
                            }
                        }}
                        startIcon={
                            <FontAwesomeIcon
                                icon={faArrowLeft} 
                                style={{ 
                                    color: 'white',
                                    fontSize: '0.65rem',
                                    paddingRight: '3px' 
                                }}
                            />
                        }
                        onClick={() => navigate("/" + speciesName + "/datamanagement")}
                    >
                        Back to Data Management
                    </Button>
                </Box>

                <Card variant='outlined' sx={{marginTop: '20px', marginBottom: '20px'}}>
                    <CardContent>
                        <Grid container spacing={1} alignItems="center">
                            <Grid item xs={12}>
                                <Button 
                                    variant="text" 
                                    startIcon={<FontAwesomeIcon icon={faSend} />}
                                    onClick={() => navigate("/" + speciesName + "/invitegroupmembers")}
                                >
                                    Invite Group Members
                                </Button>
                            </Grid>
                            <Grid item xs={12}>
                                <Box display="flex" alignItems="center">
                                    <Typography paddingLeft="5px" paddingTop="10px" fontSize={24}>
                                        Manage Group Members
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Box display="flex" alignItems="center">
                                    <Typography paddingLeft="5px">
                                        This page lists the current members of your private group. It also displays a summary of the records they have entered.
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Box display="flex" alignItems="center">
                                    <Typography paddingLeft="5px">
                                        If you cannot find a member in the list, please use the Search bar, then click the '<i>Search</i>' button.
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Box display="flex" alignItems="center">
                                    <Typography paddingLeft="5px">
                                        To remove a member, click the delete button on the far right-hand-side.
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl sx={{ mt: 3, mb: 1, pr: 1, width: "100%" }}>
                                    <InputLabel id="community-group-status-select" sx={{ mt: 1.5 }}>Community Group Status</InputLabel>
                                    <Select
                                        id="community-group-status-select"
                                        label="Community Group Status"
                                        value={selectedCommunityGroupStatus.toString()}
                                        variant="filled"
                                        size="small"
                                        onChange={handleChange}
                                    >
                                        {userGroupsConfig?.communityGroupStatuses.map((option) => (
                                            <MenuItem key={option.communityGroupStatusId} value={option.communityGroupStatusId}>
                                                {option.description}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField 
                                    id="outlined-basic" 
                                    label="User Search" 
                                    variant="filled"
                                    size="small"
                                    sx={{ mt: 3, mb:1, pl: 1, width: '100%' }}
                                    value={searchInput}
                                    onChange={handleSearchInputChange} />
                            </Grid>
                        </Grid>

                        <Box height={'10px'} />

                        {(communityGroupMembersLoading || siteEventsSightingLoading || siteEventsDamageLoading || siteEventsControlLoading) && (
                            <Box sx={{display: "flex", justifyContent: "center", width: "auto", padding: "20px"}}>
                                <CircularProgress />
                            </Box>
                        )}

                        {(!communityGroupMembersLoading && !siteEventsSightingLoading && !siteEventsDamageLoading && !siteEventsControlLoading) && (
                            <Table key={`${selectedCommunityGroupStatus}-${searchInput}`}>
                                <TableHead>
                                    <TableRow sx={{backgroundColor: '#f0f0f0'}}>
                                        {tableHeadersCells}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tableBody}
                                </TableBody>
                            </Table>
                        )}

                        <Box height="20px" />
                    </CardContent>
                </Card>
                
            </ContentWrapper>
        </Box>
    )
}

const ManageGroupMembersPage: FC = () => {
    const { user } = useContext(AuthContext);
    const { species: speciesName } = useParams();

    if (!user) {
        return <Navigate to={"/" + speciesName + "/map"} replace />
    }
    return (<ManageGroupMembersComponent />)
}

export default ManageGroupMembersPage
